import React from 'react';
import classNames from 'classnames';
import Date, { DateRefObject } from '~/app/modules/form/Date';
import { formatWorkOrderDetailDate } from '../helper';
import classes from '../style.module.scss';

const PLANNED_DATE_FORMAT = 'DD. MMM YY HH:mm';

export interface PlannedDateRangeProps {
    canEdit: boolean;
    scheduledStartDate: string;
    scheduledEndDate: string;
    plannedStartPickerDefault: string;
    plannedEndPickerDefault: string;
    minPlannedEndDate?: string;
    disabled: boolean;
    startDateRef: React.RefObject<DateRefObject>;
    endDateRef: React.RefObject<DateRefObject>;
    onStartChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onStartBlur: () => void;
    onEndChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onEndBlur: () => void;
}

const ReadonlySegment: React.FC<{ date?: string | null }> = ({ date }) => {
    const text = formatWorkOrderDetailDate(date);
    if (!text) {
        return null;
    }
    return (
        <span className={classes['plannedReadonlySegment']}>
            <span>{text}</span>
        </span>
    );
};

const PlannedDateRange: React.FC<PlannedDateRangeProps> = ({
    canEdit,
    scheduledStartDate,
    scheduledEndDate,
    plannedStartPickerDefault,
    plannedEndPickerDefault,
    minPlannedEndDate,
    disabled,
    startDateRef,
    endDateRef,
    onStartChange,
    onStartBlur,
    onEndChange,
    onEndBlur,
}) => {
    const startReadonly = formatWorkOrderDetailDate(scheduledStartDate);
    const endReadonly = formatWorkOrderDetailDate(scheduledEndDate);
    const hasAnyDate = Boolean(startReadonly || endReadonly);

    if (canEdit) {
        return (
            <div className={classes['plannedRange']}>
                <Date
                    ref={startDateRef}
                    className={classNames(classes['plannedDateInline'])}
                    inputClassName={classes['plannedDateInlineInput']}
                    name="scheduledStartDate"
                    format={PLANNED_DATE_FORMAT}
                    defaultValue={plannedStartPickerDefault}
                    disabled={disabled}
                    onChange={onStartChange}
                    onBlur={onStartBlur}
                />
                <span className={classes['plannedRangeSeparator']} aria-hidden="true">–</span>
                <Date
                    ref={endDateRef}
                    className={classNames(classes['plannedDateInline'])}
                    inputClassName={classes['plannedDateInlineInput']}
                    name="scheduledEndDate"
                    format={PLANNED_DATE_FORMAT}
                    defaultValue={plannedEndPickerDefault}
                    min={minPlannedEndDate}
                    disabled={disabled}
                    onChange={onEndChange}
                    onBlur={onEndBlur}
                />
            </div>
        );
    }

    if (!hasAnyDate) {
        return <span className={classes['value']}>-</span>;
    }

    return (
        <div className={classNames(classes['plannedRange'], classes['plannedRangeReadonly'])}>
            <ReadonlySegment date={scheduledStartDate} />
            {(startReadonly && endReadonly) && (
                <span className={classes['plannedRangeSeparator']} aria-hidden="true">–</span>
            )}
            <ReadonlySegment date={scheduledEndDate} />
        </div>
    );
};

export default PlannedDateRange;
